import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Menu,
  MenuTrigger,
  MenuPopup,
  MenuItem,
  Box,
  Text,
  Icon,
  Button,
  Checkbox,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  EntitySelectorTrigger,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  InlineRow,
  InlineRowContent,
  InlineRowActions,
  InlineValueAction,
  InlineValueCell,
  InlineValueTrigger,
  InlineEditingCell,
  toastManager,
  useIsMobile,
  useRevealOnTouchGate,
  useOutsideClick,
  ROW_RUNGS,
  PROPERTY_ROW_LABEL_RAIL,
  type InlineRowActionsVisibility,
} from "@salestrak/ui";
import {
  getDefaultValueByType,
  validateCustomFieldValue,
} from "@salestrak/custom-fields";
import { getFieldTypeMeta } from "#app/lib/constants/field-type-icons";
import { copyToClipboard } from "#app/lib/utils/copy-to-clipboard";
import { UserSelector } from "#app/components/entity-selectors";
import {
  USER_POOL_ID,
} from "#app/components/entity-selectors/hooks/use-user-pool";
import type { CustomFieldDef } from "./issue-view-types";
import {
  FIELD_MEDIA_TILE_SIZE,
  ResolvedMediaTile,
} from "#app/components/media/media-tiles";
import { MediaLightbox } from "#app/components/media/media-lightbox";
import { DeletePhotoDialog } from "#app/components/custom-fields/delete-photo-dialog";
import {
  CustomFieldRenderer,
  OptionValueChips,
  getCustomFieldDisplayValue,
  isCustomFieldValueEmpty,
} from "../custom-fields/custom-field-renderers";
import {
  CustomFieldMemberValue,
} from "../custom-fields/custom-field-member-value";
import {
  InvalidPhoneMarker,
  isFlaggedPhoneValue,
} from "../custom-fields/invalid-phone-marker";
import { UrlFavicon } from "#app/components/fields/url-favicon";
import {
  OptionSelector,
  OptionSelectorTrigger,
  type OnCreateOption,
} from "../custom-fields/option-picker";
import type { OptionActions } from "#app/lib/hooks/use-custom-field-mutations";
import {
  isMultipleField,
  NONE_OPTION_LABEL,
} from "#app/lib/custom-field-utils";
import {
  FieldDatePicker,
} from "#app/components/custom-fields/field-date-picker";
import { RichTextEditor } from "#app/components/editor/rich-text-editor";
import { normalizeRichTextValue } from "#app/lib/utils/rich-text-value";
import {
  blurActiveElement,
  getFieldTypeDescriptor,
  getProtocolHref,
  getValueActionText,
  sentenceCase,
  type FieldValueAction,
} from "#app/components/fields/field-type-descriptors";
import { useOpenAddressOnMap } from "#app/lib/hooks/use-open-address-on-map";
import type { Omittable } from "@salestrak/ui";

const TEXTAREA_LIKE_FIELD_TYPES = new Set(["paragraph"]);
const DATE_LIKE_FIELD_TYPES = new Set(["date", "time"]);
// 36px pitch: the property-list rung, tight enough that a dozen fields read
// as one list. The hairline paints on the row's last pixel (InlineRow), so
// all 36px are the row's.
export const CUSTOM_FIELD_ROW_HEIGHT = ROW_RUNGS.listRow;
// The value cell — hover ring, editing ring — stands 2px inside the pitch on
// each side (the data table's own rowHeight − 4), so the ring floats between
// the hairlines instead of sitting on them, and the hairline lands centred in
// the 4px between two rows' rings.
const CUSTOM_FIELD_VALUE_CELL_HEIGHT = CUSTOM_FIELD_ROW_HEIGHT - 4;
// The rows' lead gutter, outside the hairlines: it lines the rows up with the
// property rows above the list, and the hairlines start after it, narrower
// than the header.
const CUSTOM_FIELD_LEAD_GUTTER = 16;
export const DESKTOP_FIELD_COLUMN_WIDTH = 220;
// The desktop label column puts the values on the property rows' value
// column, so the summary reads as ONE column of values. Both lists start at
// the same edge; a property value starts after the label rail and the row's
// 8px gap, a field value after this row's 16px lead gutter and this column
// — hence the rail less 8px. The rail's share is of the whole row, and this
// column's percentage is of the row less the gutter, so the gutter's share
// is added back. A narrowing drawer shrinks both lists alike.
const { minPx: RAIL_MIN, percent: RAIL_PERCENT, maxPx: RAIL_MAX } = PROPERTY_ROW_LABEL_RAIL;
const FLUID_FIELD_COLUMN_WIDTH = `calc(clamp(${RAIL_MIN}px, ${RAIL_PERCENT}% + ${
  (RAIL_PERCENT * CUSTOM_FIELD_LEAD_GUTTER) / 100
}px, ${RAIL_MAX}px) - 8px)`;
// Free-text values wrap at rest (up to 3 lines, then an ellipsis) and the row
// grows to hold them, so a narrow drawer shows the value instead of a stub.
const WRAPPING_FIELD_TYPES = new Set(["text", "email", "url"]);
const WRAPPED_VALUE_LINES = 3;

type ActiveOverlay =
  | "menu"
  | "paragraph"
  | "select"
  | "member"
  | "date"
  | null;

type CustomFieldRowProps = Omittable<{
  cfId: string;
  field: CustomFieldDef | undefined;
  value: unknown;
  isActive?: boolean;
  fieldColumnWidth?: number;
  valueColumnWidth?: number;
  required?: boolean;
  error?: string;
  showRemove?: boolean;
  id?: string;
  highlighted?: boolean;
  beforeHighlighted?: boolean;
  /** The list's first row closes its top edge with a hairline. */
  topDivider?: boolean;
  onRowHighlightChange?: (cfId: string, open: boolean) => void;
  onInvalidStateChange?: (cfId: string, invalid: boolean) => void;
  onUpdate: (cfId: string, value: unknown) => void;
  // Photos-field delta writes (view mode only). Present → the renderer commits
  // each landed upload / removed tile as a single-photo add/remove instead of
  // a full-array overwrite, so concurrent uploads can't clobber each other.
  onAddPhoto?: (cfId: string, photoId: string) => void | Promise<void>;
  onRemovePhoto?: (cfId: string, photoId: string) => void | Promise<void>;
  onRemove: (cfId: string) => void;
  onActivate?: (cfId: string) => void;
  onDeactivate?: () => void;
  /** Inline option creation for the option types' value picker — one hook
   *  instance at the list level, threaded down. */
  onCreateOption?: OnCreateOption;
  /** In-picker option management — one hook instance at the list level,
   *  present only when the viewer holds custom_fields.manage. */
  optionActions?: OptionActions;
  // When true, the row renders the display value with no hover, no
  // cursor pointer, no editor activation, and no edit/clear/remove
  // actions. Used for view / comment access tiers.
  readOnly?: boolean;
  /** Issue name — prefixes photo download filenames in the lightbox. */
  downloadPrefix?: string;
}>;

export function CustomFieldRow({
  cfId,
  field,
  value,
  isActive = false,
  fieldColumnWidth,
  valueColumnWidth,
  required,
  error,
  showRemove = true,
  id,
  highlighted = false,
  beforeHighlighted = false,
  topDivider = false,
  onRowHighlightChange,
  onInvalidStateChange,
  onUpdate,
  onAddPhoto,
  onRemovePhoto,
  onRemove,
  onActivate,
  onDeactivate: onDeactivateRaw,
  onCreateOption,
  optionActions,
  readOnly = false,
  downloadPrefix,
}: CustomFieldRowProps) {
  const isMobile = useIsMobile();
  const [activeOverlay, setActiveOverlay] = useState<ActiveOverlay>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  // An in-place refusal the editor reported (money turning down a third
  // decimal). Paints the same label and ring as a rejected commit, but the
  // draft it kept is valid, so it never arms `invalidRef`.
  const [draftError, setDraftError] = useState<string | null>(null);
  useEffect(() => {
    if (!isActive) setDraftError(null);
  }, [isActive]);
  // Synchronous gate for the renderer's same-tick `onChangeValue` + `onDeactivate`
  // pair. Set in `commitFromRenderer` so the wrapper below can veto deactivation
  // before the corresponding state update has flushed.
  const invalidRef = useRef(false);
  // Snapshot of the value at the moment edit mode was entered, so the row's
  // menu can offer "Reset value" to revert to the last valid/empty state.
  const baselineRef = useRef<unknown>(value);
  const wasActiveRef = useRef(isActive);
  useEffect(() => {
    if (isActive && !wasActiveRef.current) {
      baselineRef.current = value;
    }
    wasActiveRef.current = isActive;
    // value intentionally omitted — we want the snapshot at the entry edge,
    // not on every value change while active.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- value is snapshotted at the entry edge only
  }, [isActive]);
  const onDeactivate = useCallback(() => {
    if (invalidRef.current) return;
    onDeactivateRaw?.();
  }, [onDeactivateRaw]);
  const commitFromRenderer = useCallback(
    (newValue: unknown) => {
      if (!field) return;
      if (newValue === undefined) {
        invalidRef.current = false;
        onInvalidStateChange?.(cfId, false);
        onUpdate(cfId, undefined);
        return;
      }
      const result = validateCustomFieldValue(field, newValue);
      if (!result.valid) {
        invalidRef.current = true;
        onInvalidStateChange?.(cfId, true);
        // The rejection is explained on the cell (its bubble) and on the
        // label (red + alert); no toast says it a third time.
        setValidationError(result.message ?? "Invalid value");
        return;
      }
      invalidRef.current = false;
      onInvalidStateChange?.(cfId, false);
      setValidationError(null);
      onUpdate(cfId, newValue);
    },
    [cfId, field, onInvalidStateChange, onUpdate],
  );
  const cellRef = useRef<HTMLDivElement | null>(null);
  const paragraphDraftRef = useRef("");
  // Gates commit so opening + closing a legacy value never dirties it.
  const paragraphEditedRef = useRef(false);
  const effectiveError = validationError ?? draftError ?? error;

  const displayValue = useMemo(
    () => (field ? getCustomFieldDisplayValue(field, value) : NONE_OPTION_LABEL),
    [field, value],
  );
  const hasValue = useMemo(
    () => (field ? !isCustomFieldValueEmpty(field, value) : false),
    [field, value],
  );
  const flaggedPhone = isFlaggedPhoneValue(field, value, hasValue);
  // Tap-to-reveal only when the primary input has no hover (true
  // tablets/phones); touchscreen laptops still hover, so the gate excludes
  // them. The row highlight flips in the same render as the reveal — the
  // dismiss effect on `highlighted` below would otherwise cancel it at once.
  const onRevealChange = useCallback(
    (open: boolean) => onRowHighlightChange?.(cfId, open),
    [cfId, onRowHighlightChange],
  );
  const {
    isTouch,
    revealed,
    setRevealed,
    absorb,
    dismiss: dismissReveal,
  } = useRevealOnTouchGate({ enabled: hasValue, onRevealChange });
  const forceHighlight = highlighted || activeOverlay !== null || revealed;
  const fieldTypeMeta = useMemo(
    () => (field ? getFieldTypeMeta(field.type) : null),
    [field],
  );
  const fieldType = field?.type;
  const isParagraphField = field
    ? TEXTAREA_LIKE_FIELD_TYPES.has(field.type)
    : false;
  const isMemberField = fieldType === "member";
  const isSingleMember = isMemberField && !isMultipleField(field?.config);
  const isCheckboxField = fieldType === "checkbox";
  // The option types: select is single BY TYPE, labels is the multi twin —
  // the legacy `multiple` config flag is not consulted.
  const isSelectField = fieldType === "select" || fieldType === "labels";
  const isPhotosField = fieldType === "photos";
  const wrapsValue = WRAPPING_FIELD_TYPES.has(fieldType ?? "");
  // A row whose content can outgrow the pitch holds it as a floor.
  const growsRow = isPhotosField || wrapsValue;
  const isMultiSelect = fieldType === "labels";
  const selectValues = useMemo(
    () =>
      isSelectField && Array.isArray(value)
        ? (value as string[]).map(String)
        : [],
    [isSelectField, value],
  );
  // Every date-like type edits in the library `DatePicker` (through the
  // field-aware `FieldDatePicker`) — the browser's own picker is never
  // reached.
  const isDateLike = fieldType ? DATE_LIKE_FIELD_TYPES.has(fieldType) : false;

  const valueActionText = useMemo(
    () => (field ? getValueActionText(field.type, value, displayValue) : null),
    [displayValue, field, value],
  );
  const protocolHref = useMemo(
    () =>
      field && hasValue ? getProtocolHref(field.type, valueActionText) : null,
    [field, hasValue, valueActionText],
  );
  const valueActions = useMemo(() => {
    if (!field) return [];
    // Drop the protocol "open" action when the value can't form a valid href.
    const all = (hasValue ? getFieldTypeDescriptor(field.type).actions : []).filter(
      (a) => a !== "open" || !!protocolHref,
    );
    // Read-only viewers can still copy the value — strip edit / clear
    // / reset actions but keep `copy`.
    return readOnly ? all.filter((a) => a === "copy") : all;
  }, [field, hasValue, protocolHref, readOnly]);
  const actionsVisibility: InlineRowActionsVisibility = (() => {
    if (valueActions.length === 0 || isActive || activeOverlay !== null)
      return "hidden";
    if (revealed) return "always";
    return "valueHoverFocus";
  })();
  const actionLabel = field ? getFieldTypeDescriptor(field.type).actionNoun : "value";
  const primaryValueCellLabel = field ? `Edit ${field.name}` : "Edit value";

  const setOverlay = useCallback(
    (next: ActiveOverlay) => {
      setActiveOverlay(next);
      onRowHighlightChange?.(cfId, next !== null);
    },
    [cfId, onRowHighlightChange],
  );

  useEffect(() => {
    if (highlighted) return;
    setActiveOverlay(null);
    setRevealed(false);
  }, [highlighted, setRevealed]);

  // Clear stale validation error when the cell exits edit mode or the
  // underlying value changes externally — otherwise the input reverts to the
  // original value on blur but the red label/icon stick around.
  useEffect(() => {
    if (!isActive) setValidationError(null);
  }, [isActive, value]);

  // Outside-click dismissal — touch reveal and inline-active editing.
  // Must also clear the parent-tracked highlight, otherwise the row stays
  // visually selected until another row is tapped.
  const handleCellOutside = useCallback(() => {
    if (isActive) onDeactivate?.();
    if (isTouch && revealed) dismissReveal();
  }, [dismissReveal, isActive, isTouch, onDeactivate, revealed]);
  useOutsideClick({
    ref: cellRef,
    active: isActive || (isTouch && revealed),
    onOutside: handleCellOutside,
  });

  const handleCopyValue = useCallback(async () => {
    if (!valueActionText) return;
    const ok = await copyToClipboard(valueActionText);
    if (ok) toastManager.add({ title: `${sentenceCase(actionLabel)} copied`, type: "success" });
    else toastManager.add({ title: `Failed to copy ${actionLabel}`, type: "error" });
    blurActiveElement();
  }, [actionLabel, valueActionText]);

  const handleClearValue = useCallback(() => {
    if (!field) return;
    invalidRef.current = false;
    onInvalidStateChange?.(cfId, false);
    setValidationError(null);
    onDeactivate?.();
    onUpdate(cfId, getDefaultValueByType(field.type) ?? null);
    blurActiveElement();
  }, [cfId, field, onDeactivate, onInvalidStateChange, onUpdate]);

  const handleResetValue = useCallback(() => {
    if (!field) return;
    invalidRef.current = false;
    onInvalidStateChange?.(cfId, false);
    setValidationError(null);
    setOverlay(null);
    onUpdate(cfId, baselineRef.current);
    onDeactivate?.();
    blurActiveElement();
  }, [cfId, field, onDeactivate, onInvalidStateChange, onUpdate, setOverlay]);

  const handleParagraphPopoverOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        if (absorb(true)) return;
        // Display-only normalize; reset edited flag so a no-op open/close never persists it.
        paragraphDraftRef.current = normalizeRichTextValue(value);
        paragraphEditedRef.current = false;
        onDeactivate?.();
        setOverlay("paragraph");
        return;
      }
      if (field && paragraphEditedRef.current) {
        onUpdate(cfId, paragraphDraftRef.current);
      }
      blurActiveElement();
      setOverlay(null);
    },
    [absorb, cfId, field, onDeactivate, onUpdate, setOverlay, value],
  );

  const openParagraphPopover = useCallback(
    () => setOverlay("paragraph"),
    [setOverlay],
  );


  // Opens the protocol (mailto / tel / website) for protocol fields. Wired to
  // a dedicated action button — the cell click itself always edits.
  const openAddressOnMap = useOpenAddressOnMap("detail");
  const handleOpenOnMap = useCallback(() => {
    if (!valueActionText) return;
    blurActiveElement();
    void openAddressOnMap({ wholeAddress: valueActionText });
  }, [openAddressOnMap, valueActionText]);

  const handleOpenProtocol = useCallback(() => {
    if (!field || !protocolHref || typeof window === "undefined") return;
    onRowHighlightChange?.(cfId, true);
    blurActiveElement();
    if (field.type === "url") {
      window.open(protocolHref, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = protocolHref;
    }
  }, [cfId, field, onRowHighlightChange, protocolHref]);

  const handleCellPress = useCallback(() => {
    if (!field || isActive) return;
    if (absorb(true)) return;
    if (isParagraphField) {
      openParagraphPopover();
      return;
    }
    onActivate?.(cfId);
  }, [
    absorb,
    cfId,
    field,
    isActive,
    isParagraphField,
    onActivate,
    openParagraphPopover,
  ]);

  const handleParagraphDraftChange = useCallback((html: string) => {
    // onUpdate fires only on real user edits (never on content load) — mark dirty.
    paragraphDraftRef.current = html;
    paragraphEditedRef.current = true;
  }, []);

  const paragraphPopoverOpenRef = useRef(false);
  paragraphPopoverOpenRef.current = activeOverlay === "paragraph";
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;
  const cfIdRef = useRef(cfId);
  cfIdRef.current = cfId;
  const fieldExistsRef = useRef(!!field);
  fieldExistsRef.current = !!field;
  useEffect(
    () => () => {
      if (
        paragraphPopoverOpenRef.current &&
        fieldExistsRef.current &&
        paragraphEditedRef.current
      ) {
        onUpdateRef.current(cfIdRef.current, paragraphDraftRef.current);
      }
    },
    [],
  );

  const renderActionButton = (action: FieldValueAction) => {
    if (action === "open") {
      const descriptor = field ? getFieldTypeDescriptor(field.type) : undefined;
      const verb = descriptor?.protocolVerb ?? "Open";
      return (
        <InlineValueAction
          key={action}
          icon={descriptor?.protocolIcon ?? "external-link"}
          label={`${verb} ${actionLabel}`}
          tooltip={descriptor?.protocolTooltip ?? verb}
          onSelect={handleOpenProtocol}
        />
      );
    }
    if (action === "map") {
      return (
        <InlineValueAction
          key={action}
          icon="map-pin"
          label={`Open ${actionLabel} in Map`}
          tooltip="Open in Map"
          onSelect={handleOpenOnMap}
        />
      );
    }
    if (action === "copy") {
      return (
        <InlineValueAction
          key={action}
          icon="copy"
          label={`Copy ${actionLabel}`}
          tooltip="Copy"
          onSelect={() => void handleCopyValue()}
        />
      );
    }
    if (action === "fullscreen") {
      return (
        <InlineValueAction
          key={action}
          icon="maximize-2"
          label={`View ${actionLabel} full screen`}
          tooltip="Full screen"
          onSelect={() => {
            setLightboxIndex(0);
            setLightboxOpen(true);
          }}
        />
      );
    }
    return (
      <InlineValueAction
        key={action}
        icon="x"
        label={`Clear ${actionLabel}`}
        tooltip="Clear"
        onSelect={handleClearValue}
      />
    );
  };

  // The display nodes set no height: the InlineValueCell sizes the row and
  // its 1px-bordered trigger stretches them — a display sized to the OUTER
  // row height overflowed the trigger by 2px and sat 1px low.
  const valueDisplay =
    isMemberField ? (
      // Members render as themselves: a property row names ONE member (avatar
      // + label) and collapses two or more to the avatar-only stack.
      <Box fill="flex" direction="row" align="center" paddingX="md">
        <CustomFieldMemberValue
          value={value}
          surface="property"
          hoverDisabled={activeOverlay === "member"}
        />
      </Box>
    ) : isSelectField && field && hasValue ? (
      // Option values read as chips at rest — tonal for Single Select, a
      // one-line soft cluster (as many as fit, then +N) for Multi Select.
      <Box fill="flex" direction="row" align="center" paddingX="md">
        <OptionValueChips field={field} value={value} multiple={isMultiSelect} />
      </Box>
    ) : (
      <Box
        fill="flex"
        direction="row"
        align="center"
        gap="xs"
        paddingX="md"
        // A wrapped value keeps a 4px inset off the cell's ring; one line
        // (20px + 8px) still fits the 32px cell, so no row grows early.
        paddingY={wrapsValue ? "xs" : undefined}
      >
        {field?.type === "url" && hasValue && <UrlFavicon url={String(value)} />}
        <Box fill={flaggedPhone ? undefined : "flex"} shrink minWidth={0}>
          <Text
            size="sm"
            // The empty marker sits on the faint rung, two steps under the
            // full-ink field name, so an unset row reads as a name and a
            // quiet placeholder rather than two equal texts.
            variant={
              flaggedPhone
                ? "destructive"
                : displayValue === NONE_OPTION_LABEL
                  ? "faint"
                  : undefined
            }
            truncate={wrapsValue ? WRAPPED_VALUE_LINES : 1}
          >
            {displayValue}
          </Text>
        </Box>
        {flaggedPhone && <InvalidPhoneMarker />}
      </Box>
    );

  const valueCellActions =
    valueActions.length > 0 ? (
      <Box
        position="absolute"
        right={4}
        top={0}
        bottom={0}
        justify="center"
      >
        <InlineRowActions chrome="chip" visibility={actionsVisibility}>
          {valueActions.map(renderActionButton)}
        </InlineRowActions>
      </Box>
    ) : null;

  const renderValueCell = () => {
    if (!field) {
      return (
        <Box fill="flex" justify="center" paddingX="md" paddingY="sm">
          <Text size="sm" variant="faint">
            {"\u2014"}
          </Text>
        </Box>
      );
    }

    // Read-only short-circuit. Every field type collapses to the same
    // plain display — no Pressable, no edit affordances, no cursor
    // pointer. The editor branches below (including the checkbox toggle)
    // are skipped so a view/comment-tier viewer can't activate them.
    // Wrapped in `InlineValueCell` (no `InlineValueTrigger`) so the
    // `group/inline-value-cell` class is present — the absolute-positioned
    // copy button still reveals on hover via `group-hover/inline-value-cell`.
    if (readOnly) {
      if (isPhotosField) {
        return (
          <InlineValueCell
          minHeight={CUSTOM_FIELD_VALUE_CELL_HEIGHT}
          bleedEnd
          active={activeOverlay !== null}
        >
            <Box
              fill="flex"
              direction="row"
              wrap
              gap="sm"
              paddingY="2xs"
              paddingX="md"
              align="center"
            >
              {(Array.isArray(value) ? value : [])
                .filter((v): v is string => typeof v === "string")
                .map((photoId, index) => (
                  <ResolvedMediaTile
                    key={`${photoId}-${index}`}
                    photoId={photoId}
                    size={FIELD_MEDIA_TILE_SIZE}
                  />
                ))}
            </Box>
            {valueCellActions}
          </InlineValueCell>
        );
      }
      return (
        <InlineValueCell
          height={wrapsValue ? undefined : CUSTOM_FIELD_VALUE_CELL_HEIGHT}
          minHeight={wrapsValue ? CUSTOM_FIELD_VALUE_CELL_HEIGHT : undefined}
          bleedEnd
          active={activeOverlay !== null}
        >
          {valueDisplay}
          {valueCellActions}
        </InlineValueCell>
      );
    }

    // Checkbox toggles in place — no edit mode. The box always renders,
    // even unset (null), rather than the '—' empty marker. First press
    // writes `true`; further presses toggle true/false.
    if (isCheckboxField) {
      return (
        <InlineValueCell
          height={CUSTOM_FIELD_VALUE_CELL_HEIGHT}
          bleedEnd
          active={activeOverlay !== null}
        >
          <InlineValueTrigger
            // The box below only draws the state, so the button carries it.
            aria-checked={value === true}
            aria-label={field.name}
            onClick={() => onUpdate(cfId, !(value === true))}
            role="checkbox"
          >
            <Box
              direction="row"
              align="center"
              justify="start"
              paddingX="md"
            >
              {/* Visual-only: the InlineValueTrigger button owns the toggle,
                  so the box only draws the state — `disabled` would dim it. */}
              <Checkbox checked={value === true} nonInteractive />
            </Box>
          </InlineValueTrigger>
          {valueCellActions}
        </InlineValueCell>
      );
    }

    // Photos edit in place — thumbnails live directly in the cell (no
    // text summary, no activation step); the row grows with the grid.
    if (isPhotosField) {
      const mediaIds = Array.isArray(value)
        ? value.filter((v): v is string => typeof v === "string")
        : [];
      const removePhoto = (photoId: string) =>
        onRemovePhoto
          ? onRemovePhoto(cfId, photoId)
          : onUpdate(cfId, mediaIds.filter((id) => id !== photoId));
      return (
        <InlineValueCell
          minHeight={CUSTOM_FIELD_VALUE_CELL_HEIGHT}
          bleedEnd
          active={activeOverlay !== null}
        >
          <Box
            fill="flex"
            direction="row"
            paddingLeft={mediaIds.length ? "md" : "sm"}
            marginLeft={mediaIds.length ? undefined : "2xs"}
            paddingRight="md"
          >
            <CustomFieldRenderer
              field={field}
              value={value}
              onChangeValue={(next) => onUpdate(cfId, next)}
              onAddPhoto={onAddPhoto ? (photoId) => onAddPhoto(cfId, photoId) : undefined}
              onRemovePhoto={onRemovePhoto ? (photoId) => onRemovePhoto(cfId, photoId) : undefined}
              onOpenPhoto={(index) => {
                setLightboxIndex(index);
                setLightboxOpen(true);
              }}
            />
          </Box>
          {valueCellActions}
          <MediaLightbox
            media={mediaIds.map((id) => ({ id, contentType: "image/*" }))}
            open={lightboxOpen}
            onOpenChange={setLightboxOpen}
            initialIndex={lightboxIndex}
            title={field.name}
            downloadPrefix={downloadPrefix}
            onRemove={(photoId) => (isMobile ? setConfirmRemoveId(photoId) : removePhoto(photoId))}
          />
          {isMobile ? (
            <DeletePhotoDialog
              open={confirmRemoveId !== null}
              onOpenChange={(open) => {
                if (!open) setConfirmRemoveId(null);
              }}
              onConfirm={() => {
                if (confirmRemoveId) removePhoto(confirmRemoveId);
              }}
            />
          ) : null}
        </InlineValueCell>
      );
    }

    if (
      isActive &&
      !isParagraphField &&
      !isMemberField &&
      !isSelectField &&
      !isDateLike
    ) {
      return (
        // A rejected commit is explained on the cell itself, the way the
        // table cell does it (EditableValueCell); a draft error is the
        // editor's own bubble, so it does not open a second one here.
        <Tooltip open={validationError !== null}>
          <TooltipTrigger
            render={
              <InlineEditingCell
                // The rest cell's sizing: a wrapping field's row can stand
                // taller than the pitch, and a fixed height would pin the
                // editor to the top of the stretched column.
                height={wrapsValue ? undefined : CUSTOM_FIELD_VALUE_CELL_HEIGHT}
                minHeight={wrapsValue ? CUSTOM_FIELD_VALUE_CELL_HEIGHT : undefined}
                bleedEnd
                // The same inset the rest display wears, so the text holds
                // still when editing starts; table-surface editors carry
                // none of their own.
                paddingX="md"
                invalid={validationError !== null || draftError !== null}
              />
            }
          >
            <CustomFieldRenderer
              field={field}
              value={value}
              onChangeValue={commitFromRenderer}
              isActive={isActive}
              onDeactivate={onDeactivate}
              autoFocus
              invalid={!!validationError}
              onDraftErrorChange={setDraftError}
            />
          </TooltipTrigger>
          {validationError !== null ? (
            <TooltipPopup side="top" align="start">
              {validationError}
            </TooltipPopup>
          ) : null}
        </Tooltip>
      );
    }

    if (isMemberField) {
      const handleMemberOpenChange = (next: boolean) => {
        if (absorb(next)) return;
        setOverlay(next ? "member" : null);
      };
      // Member values are always id arrays; single-mode caps at one element.
      const memberTrigger = (
        <InlineValueCell
          height={CUSTOM_FIELD_VALUE_CELL_HEIGHT}
          bleedEnd
          active={activeOverlay !== null}
        >
          <EntitySelectorTrigger
            render={
              <InlineValueTrigger aria-label={`Edit ${field.name}`}>
                {valueDisplay}
              </InlineValueTrigger>
            }
          />
          {valueCellActions}
        </InlineValueCell>
      );
      return (
        <UserSelector
          title={field.name}
          value={value as string | string[] | null}
          onValueChange={(ids) =>
            onUpdate(cfId, isSingleMember ? (ids[0] ?? null) : ids)
          }
          maxSelections={isSingleMember ? 1 : undefined}
          open={activeOverlay === "member"}
          onOpenChange={handleMemberOpenChange}
        >
          {memberTrigger}
          <EntitySelectorContent align="start">
            <EntitySelectorInput />
            <EntitySelectorPool value={USER_POOL_ID} />
          </EntitySelectorContent>
        </UserSelector>
      );
    }

    if (isDateLike) {
      const handleDateOpenChange = (next: boolean) => {
        if (absorb(next)) return;
        setOverlay(next ? "date" : null);
      };
      return (
        <FieldDatePicker
          field={field}
          value={value}
          onValueChange={(next) => onUpdate(cfId, next)}
          open={activeOverlay === "date"}
          onOpenChange={handleDateOpenChange}
          align="start"
        >
          <InlineValueCell
          height={CUSTOM_FIELD_VALUE_CELL_HEIGHT}
          bleedEnd
          active={activeOverlay !== null}
        >
            <PopoverTrigger
              render={
                <InlineValueTrigger aria-label={`Edit ${field.name}`} />
              }
            >
              {valueDisplay}
            </PopoverTrigger>
            {valueCellActions}
          </InlineValueCell>
        </FieldDatePicker>
      );
    }

    if (isSelectField) {
      const handleOpenChange = (next: boolean) => {
        if (absorb(next)) return;
        setOverlay(next ? "select" : null);
      };
      // The searchable option picker (chips, colors, and the gated inline
      // "Create <name>" row) replaces the plain Base UI Select for both
      // option types.
      return (
        <OptionSelector
          field={field}
          values={selectValues}
          multiple={isMultiSelect}
          onValuesChange={(vals) => onUpdate(cfId, vals)}
          open={activeOverlay === "select"}
          onOpenChange={handleOpenChange}
          onCreateOption={onCreateOption}
          optionActions={optionActions}
        >
          {/* minHeight, not height: a multi-select chip cluster wraps and
              the row grows with it (the photos row sets the precedent). */}
          <InlineValueCell
          minHeight={CUSTOM_FIELD_VALUE_CELL_HEIGHT}
          bleedEnd
          active={activeOverlay !== null}
        >
            <OptionSelectorTrigger
              render={
                <InlineValueTrigger aria-label={`Edit ${field.name}`}>
                  {valueDisplay}
                </InlineValueTrigger>
              }
            />
            {valueCellActions}
          </InlineValueCell>
        </OptionSelector>
      );
    }

    if (isParagraphField) {
      const editor = (
        <RichTextEditor
          label={field.name}
          compact
          mentions
          autoFocus
          content={paragraphDraftRef.current}
          placeholder="Write"
          shortcutHints
          onUpdate={handleParagraphDraftChange}
          contentPaddingX="sm"
          contentPaddingY="sm"
        />
      );
      return (
        // `responsive`: a bottom sheet on a phone, captioned with the
        // field's name; the editor is the sheet's body.
        <Popover
          open={activeOverlay === "paragraph"}
          onOpenChange={handleParagraphPopoverOpenChange}
          responsive
        >
          <InlineValueCell
          height={CUSTOM_FIELD_VALUE_CELL_HEIGHT}
          bleedEnd
          active={activeOverlay !== null}
        >
            <PopoverTrigger
              render={
                <InlineValueTrigger aria-label={`Edit ${field.name}`} />
              }
            >
              {valueDisplay}
            </PopoverTrigger>
            {valueCellActions}
          </InlineValueCell>
          <PopoverPopup
            align="start"
            side="bottom"
            padding="none"
            sheetTitle={field.name}
            width="panel"
          >
            {editor}
          </PopoverPopup>
        </Popover>
      );
    }

    return (
      <InlineValueCell
          height={wrapsValue ? undefined : CUSTOM_FIELD_VALUE_CELL_HEIGHT}
          minHeight={wrapsValue ? CUSTOM_FIELD_VALUE_CELL_HEIGHT : undefined}
          bleedEnd
          active={activeOverlay !== null}
        >
        <InlineValueTrigger
          aria-label={primaryValueCellLabel}
          onClick={handleCellPress}
        >
          {valueDisplay}
        </InlineValueTrigger>
        {valueCellActions}
      </InlineValueCell>
    );
  };

  return (
    <InlineRow
      id={id}
      height={growsRow ? undefined : CUSTOM_FIELD_ROW_HEIGHT}
      minHeight={growsRow ? CUSTOM_FIELD_ROW_HEIGHT : undefined}
      highlighted={forceHighlight}
      beforeHighlighted={beforeHighlighted}
      topDivider={topDivider}
      // An empty gutter: `null` still reserves it (undefined drops it).
      leading={null}
      leadingWidth={CUSTOM_FIELD_LEAD_GUTTER}
    >
      {/* Label */}
      <InlineRowContent
        direction="row"
        // One pitch tall, with no vertical padding: the 28px actions chip
        // plus a padding would outgrow the pitch and push a growing row
        // taller than its siblings. A fixed height also opts the label out
        // of the row's stretch, so on a wrapped value it stays on the
        // first line.
        height={CUSTOM_FIELD_ROW_HEIGHT}
        align="center"
        gap="sm"
        paddingLeft="sm"
        width={fieldColumnWidth ?? FLUID_FIELD_COLUMN_WIDTH}
        shrink={false}
        onClick={
          !readOnly && isMobile && showRemove ? () => setOverlay("menu") : undefined
        }
      >
        <Box fill="flex" direction="row" align="center" gap="sm">
          {(() => {
            // An error takes the type glyph's slot rather than a slot of its
            // own, so the name keeps its full width while the field is red.
            const glyph = effectiveError ? (
              <Tooltip>
                <TooltipTrigger render={<Box direction="row" align="center" shrink={false} />}>
                  <Icon name="alert-circle" size="xs" color="destructive" />
                </TooltipTrigger>
                {/* Above the row, never across the value it explains. */}
                <TooltipPopup side="top" align="start">
                  <Text size="xs">{effectiveError}</Text>
                </TooltipPopup>
              </Tooltip>
            ) : fieldTypeMeta ? (
              // 14px (`xs`): the type glyph is a key beside the name,
              // not a 16px entity visual. Muted, the same ink the
              // property rows' glyphs above wear.
              <Icon name={fieldTypeMeta.icon} size="xs" color="muted" />
            ) : null;
            const labelBody = (
              <Box fill="flex" direction="row" align="center" gap="sm">
                {glyph}
                {/* Full foreground: the name is the row's key, the glyph and
                    the value carry the quieter rungs. A name the column
                    clips shows in full on hover. */}
                <Text
                  size="sm"
                  weight="normal"
                  truncate={1}
                  overflowTooltip={!field?.description}
                  variant={effectiveError ? "destructive" : undefined}
                >
                  {field?.name ?? cfId}
                  {required && <Text variant="destructive"> *</Text>}
                </Text>
              </Box>
            );
            // A field description surfaces as a hover tooltip on its label,
            // which names the field in full as well.
            return field?.description ? (
              // The tooltip trigger renders its own element, so the label body
              // gets a Box host rather than becoming a button nobody can act on.
              <Tooltip>
                <TooltipTrigger render={<Box direction="row" align="center" fill="flex" />}>
                  {labelBody}
                </TooltipTrigger>
                <TooltipPopup side="top" align="start">
                  <Box gap="2xs" maxWidth={280}>
                    <Text size="xs" weight="semibold">
                      {field.name}
                    </Text>
                    <Text size="xs" variant="muted">
                      {field.description}
                    </Text>
                  </Box>
                </TooltipPopup>
              </Tooltip>
            ) : (
              labelBody
            );
          })()}
        </Box>
        {showRemove && (
          // Floats over the label's end rather than holding a 28px slot of
          // its own: a reserved slot clipped every name to make room for a
          // button that shows only on hover.
          <InlineRowActions
            width={28}
            height={28}
            position="absolute"
            right={0}
            visibility={forceHighlight ? "always" : "hover"}
          >
            <Menu
              open={activeOverlay === "menu"}
              onOpenChange={(open: boolean) => setOverlay(open ? "menu" : null)}
            >
              {/* TooltipTrigger inside the menu trigger's render chain — the
                  data-table row kebab's recipe; same squared 24px rung as the
                  value actions. */}
              <Tooltip>
                <MenuTrigger
                  render={
                    <TooltipTrigger
                      render={
                        <Button
                          variant="outline"
                          size="icon-xs"
                          shape="square"
                          aria-label={`Open ${field?.name ?? "custom field"} menu`}
                        />
                      }
                    />
                  }
                >
                  <Icon name="ellipsis" size="md" color="muted" />
                </MenuTrigger>
                <TooltipPopup side="top">
                  <Text size="xs">Field options</Text>
                </TooltipPopup>
              </Tooltip>
              <MenuPopup align="start">
                {isActive ? (
                  <MenuItem onClick={handleResetValue}>
                    <Icon name="rotate" size="md" />
                    <Text>Reset value</Text>
                  </MenuItem>
                ) : null}
                <MenuItem onClick={() => onRemove(cfId)}>
                  <Icon name="x" size="md" />
                  <Text>Remove field</Text>
                </MenuItem>
              </MenuPopup>
            </Menu>
          </InlineRowActions>
        )}
      </InlineRowContent>

      {/* Value: the cell stands 2px inside the row on each side, so its ring
          never touches a hairline. */}
      <InlineRowContent
        ref={cellRef}
        fill={valueColumnWidth ? undefined : "flex"}
        width={valueColumnWidth}
        shrink={!valueColumnWidth}
        direction="row"
        align="stretch"
        paddingY="2xs"
      >
        {renderValueCell()}
      </InlineRowContent>
    </InlineRow>
  );
}
